import axios, { AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import { showApiError } from './vueSweetalert2';
import { getBaseUrl } from './getBaseUrl';
import { safeStorage } from './safeStorage';
import { localizeNetworkError } from './networkError';

type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';

interface ApiCallParams {
    method: HttpMethod;
    url: string;
    data?: Record<string, unknown> | FormData;
    params?: Record<string, unknown>;
    headers?: Record<string, string> | null;
    responseType?: AxiosRequestConfig['responseType'];
    apiVersion?: string;
    showAlert?: boolean;
    /**
     * Aditivo. Default `false` (comportamiento intacto para todo el portal: un 403
     * sigue cerrando sesión). En `true`, un 403 de ESTA llamada no limpia
     * localStorage ni redirige, y tampoco dispara el Swal genérico de error —
     * se usa en endpoints de permisos sobre OTRO usuario (ej. "Mi equipo"),
     * donde el 403 significa "no tienes permiso sobre ESE auxiliar/sección",
     * no "tu apikey venció". El error se propaga (throw/reject) al llamador,
     * que decide cómo mostrarlo. Otros errores (red, 500) de la misma llamada
     * siguen respetando `showAlert` normalmente.
     */
    skipAuthRedirect?: boolean;
}

const DEDUPLICATE_METHODS = ['get', 'post'];

interface PendingListener {
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
    isCancelled: boolean;
}

interface SharedRequest {
    source: CancelTokenSource;
    listeners: Set<PendingListener>;
}

const activeRequests = new Map<string, SharedRequest>();

function getRequestHash(method: string, url: string, data: unknown, params: unknown): string | null {
    if (!DEDUPLICATE_METHODS.includes(method.toLowerCase())) {
        return null;
    }
    if (data instanceof FormData) {
        return null;
    }
    try {
        const serializedData = data ? JSON.stringify(data) : '';
        const serializedParams = params ? JSON.stringify(params) : '';
        return `${method.toLowerCase()}:${url}:${serializedData}:${serializedParams}`;
    } catch (e) {
        console.error('error: ', e);
        return null;
    }
}

export function apiCall({
    method,
    url,
    data = {},
    params = {},
    headers = null,
    responseType = 'json',
    apiVersion = 'v2',
    showAlert = true,
    skipAuthRedirect = false
}: ApiCallParams): {
    request: Promise<unknown>;
    cancel: () => void;
} {
    const apikey = safeStorage.getItem('apikey');

    const axiosConfigHeaders = { 'X-API-KEY': apikey || '' };
    const finalHeaders = headers == null ? { ...axiosConfigHeaders } : { ...axiosConfigHeaders, ...headers };

    const baseURL = getBaseUrl(apiVersion);

    if (!baseURL) {
        throw new Error(`Versión de API inválida: ${apiVersion}`);
    }

    const hash = getRequestHash(method, url, data, params);

    // Si no califica para deduplicación, ejecutamos la petición axios estándar
    if (!hash) {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        const config: AxiosRequestConfig = {
            method,
            url,
            baseURL,
            data,
            headers: finalHeaders,
            params,
            cancelToken: source.token,
            responseType
        };

        const request = axios(config)
            .then((response: AxiosResponse) => response.data)
            .catch((error) => {
                if (axios.isCancel(error)) {
                    console.error('Solicitud cancelada:', error.message);
                    return undefined;
                }
                localizeNetworkError(error);
                if (error.response?.status === 403) {
                    if (skipAuthRedirect) {
                        // El llamador decide cómo mostrar este error (ej. panel bloqueado
                        // con mensaje propio) — no se dispara el Swal genérico aquí.
                        console.error('403 en llamada con skipAuthRedirect: no se cierra sesión.', url);
                        throw error;
                    }
                    console.error('API Key inválida o expirada. Cerrando sesión...');
                    try {
                        localStorage.clear();
                    } catch {
                        /* almacenamiento no disponible */
                    }
                    window.location.href = '/';
                    return;
                }
                if (showAlert) {
                    showApiError(url, null, error);
                }
                console.error('Error en la solicitud API:', error.message);
                throw error;
            });

        return {
            request,
            cancel: () => source.cancel('Operación cancelada por el usuario')
        };
    }

    // 1. Si no existe una solicitud idéntica en vuelo, la iniciamos
    let shared: SharedRequest;
    const existing = activeRequests.get(hash);

    if (!existing) {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        const config: AxiosRequestConfig = {
            method,
            url,
            baseURL,
            data,
            headers: finalHeaders,
            params,
            cancelToken: source.token,
            responseType
        };

        const listeners = new Set<PendingListener>();

        axios(config)
            .then((response: AxiosResponse) => {
                const responseData = response.data;
                for (const listener of listeners) {
                    if (!listener.isCancelled) {
                        listener.resolve(responseData);
                    }
                }
                activeRequests.delete(hash);
                return responseData;
            })
            .catch((error) => {
                localizeNetworkError(error);
                if (axios.isCancel(error)) {
                    console.error('Solicitud cancelada:', error.message);
                } else if (error.response?.status === 403) {
                    if (skipAuthRedirect) {
                        // El llamador decide cómo mostrar este error — sin Swal genérico.
                        console.error('403 en llamada con skipAuthRedirect: no se cierra sesión.', url);
                    } else {
                        console.error('API Key inválida o expirada. Cerrando sesión...');
                        try {
                            localStorage.clear();
                        } catch {
                            /* almacenamiento no disponible */
                        }
                        window.location.href = '/';
                    }
                } else {
                    if (showAlert) {
                        showApiError(url, null, error);
                    }
                    console.error('Error en la solicitud API:', error.message);
                }

                for (const listener of listeners) {
                    if (!listener.isCancelled) {
                        listener.reject(error);
                    }
                }
                activeRequests.delete(hash);
            });

        shared = { source, listeners };
        activeRequests.set(hash, shared);
    } else {
        shared = existing;
    }

    // 2. Creamos una promesa diferida (deferred) local para el llamador actual
    let localResolve!: (value: unknown) => void;
    let localReject!: (reason?: unknown) => void;
    const localPromise = new Promise<unknown>((resolve, reject) => {
        localResolve = resolve;
        localReject = reject;
    });

    const listener: PendingListener = {
        resolve: localResolve,
        reject: localReject,
        isCancelled: false
    };

    shared.listeners.add(listener);

    const cancel = () => {
        if (listener.isCancelled) return;
        listener.isCancelled = true;
        shared.listeners.delete(listener);

        listener.reject(new axios.Cancel('Operación cancelada por el usuario local'));

        // Si ya no quedan oyentes interesados, cancelamos la petición real
        if (shared.listeners.size === 0) {
            shared.source.cancel('Todos los oyentes cancelaron la operación');
            activeRequests.delete(hash);
        }
    };

    return {
        request: localPromise,
        cancel
    };
}
